import { useEffect, useMemo, useRef, useState } from 'react';
import { logger } from '@zep/core-logger';
import { StandardCard } from '../../achievement-standard-card/components/StandardCard';
import { StandardLoadingState } from '../../achievement-standard-card/components/StandardLoadingState';
import type { AchievementStandard } from '../../achievement-standard-card/types';
import { useStandardPickerCategory } from '../../achievement-standard-picker/context/StandardPickerCategoryProvider';
import { useGradeOptions } from '../hooks/useGradeOptions';
import { useSearchFilter } from '../hooks/useSearchFilter';
import type { SearchFilter as SearchFilterType } from '../types';
import { SearchEmpty } from './SearchEmpty';
import { SearchFilterBar } from './SearchFilterBar';
import { SearchInput } from './SearchInput';
import { SearchPlaceholder } from './SearchPlaceholder';
interface AchievementStandardSearchProps {
  searchResults: AchievementStandard[];
  isLoading: boolean;
  selectedAchievementStandards: AchievementStandard[];
  onToggle: (achievementStandard: AchievementStandard) => void;
  onSearch: (filter: SearchFilterType) => void;
  isMaxReached: boolean;
}

/**
 * 성취기준 검색 컴포넌트
 * 검색 필터(키워드, 학년, 과목)와 검색 결과 목록을 표시
 */
export const AchievementStandardSearch = ({
  searchResults,
  isLoading,
  selectedAchievementStandards,
  onToggle,
  onSearch,
  isMaxReached
}: AchievementStandardSearchProps) => {
  const [submittedSearch, setSubmittedSearch] = useState('');
  const categoryContext = useStandardPickerCategory();
  const {
    schoolInfoTree
  } = categoryContext;
  const gradeOptions = useGradeOptions(schoolInfoTree);
  const handleSearch = (filter: SearchFilterType) => {
    logger.track('search_ai_std', {
      category_ids: filter.categoryIds,
      query: filter.query
    });
    onSearch(filter);
    setSubmittedSearch(filter.query);
  };
  const {
    state,
    handleKeywordChange,
    handleGradeChange,
    handleSubjectChange,
    handleSearchSubmit,
    handleSearchClear
  } = useSearchFilter(gradeOptions, handleSearch, categoryContext);

  // Auto-search on mount when category is provided
  const hasAutoSearched = useRef(false);
  useEffect(() => {
    if (!hasAutoSearched.current) {
      categoryContext.initialSchoolInfoId && handleSearchSubmit();
      hasAutoSearched.current = true;
    }
  }, [handleSearchSubmit, categoryContext.initialSchoolInfoId]);

  // Auto-search on grade/subject filter change (after initial mount)
  // useEffect를 사용하여 stale closure 문제 방지:
  // setTimeout 방식은 dispatch 이전 렌더의 handleSearchSubmit을 캡처하여
  // 변경 전 state로 검색을 실행하는 문제가 있었음.
  // useEffect는 state 업데이트가 반영된 렌더 이후 실행되므로 최신 state를 보장함.
  const isFirstFilterEffect = useRef(true);
  useEffect(() => {
    if (isFirstFilterEffect.current) {
      isFirstFilterEffect.current = false;
      return;
    }
    if (hasAutoSearched.current) {
      handleSearchSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.gradeOption, state.subjectGroupId]);
  return <div className="flex h-full flex-col" data-sentry-component="AchievementStandardSearch" data-sentry-source-file="AchievementStandardSearch.tsx">
      {/* Search Container - Figma 스펙: bg-gray-50, pt-md pb-sm px-lg, gap-sm */}
      <div className="flex flex-col gap-sm bg-gray-50 px-md pb-sm pt-md mobile-lg:px-lg">
        {/* 검색 입력 */}
        <SearchInput keyword={state.keyword} hasKeywordChanged={state.hasKeywordChanged} onKeywordChange={handleKeywordChange} onSearchSubmit={handleSearchSubmit} onSearchClear={handleSearchClear} data-sentry-element="SearchInput" data-sentry-source-file="AchievementStandardSearch.tsx" />

        {/* 학년 + 과목 필터 */}
        <SearchFilterBar gradeOptions={gradeOptions} state={state} onGradeChange={handleGradeChange} onSubjectChange={handleSubjectChange} data-sentry-element="SearchFilterBar" data-sentry-source-file="AchievementStandardSearch.tsx" />
      </div>

      {/* 검색 결과 - Figma 스펙: bg-gray-50, 전체 flex-1 영역 */}
      <div className="flex-1 overflow-auto bg-gray-50 px-md mobile-lg:px-lg">
        <AchievementStandardSearchContent isLoading={isLoading} submittedSearch={submittedSearch} searchResults={searchResults} selectedAchievementStandards={selectedAchievementStandards} onToggle={onToggle} isMaxReached={isMaxReached} data-sentry-element="AchievementStandardSearchContent" data-sentry-source-file="AchievementStandardSearch.tsx" />
      </div>
    </div>;
};

/**
 * 검색 결과 컨텐츠 컴포넌트
 * 로딩, 빈 결과, 플레이스홀더, 결과 목록 중 하나를 표시
 */
const AchievementStandardSearchContent = ({
  isLoading,
  submittedSearch,
  searchResults,
  selectedAchievementStandards,
  onToggle,
  isMaxReached
}: {
  isLoading: boolean;
  submittedSearch: string;
  searchResults: AchievementStandard[];
  selectedAchievementStandards: AchievementStandard[];
  onToggle: (achievementStandard: AchievementStandard) => void;
  isMaxReached: boolean;
}) => {
  const selectedStandardCodes = useMemo(() => selectedAchievementStandards.map(standard => standard.code), [selectedAchievementStandards]);
  if (isLoading) {
    return <StandardLoadingState />;
  }
  if (submittedSearch && searchResults.length === 0) {
    return <SearchEmpty promptQuery={submittedSearch} />;
  }
  if (!submittedSearch && searchResults.length === 0) {
    return <SearchPlaceholder />;
  }
  return <div className="flex flex-col gap-sm pb-md" data-sentry-component="AchievementStandardSearchContent" data-sentry-source-file="AchievementStandardSearch.tsx">
      {searchResults.map(standard => {
      const isSelected = selectedStandardCodes.includes(standard.code);
      const isDisabled = !isSelected && isMaxReached;
      return <StandardCard key={standard.code} standard={standard} isSelected={isSelected} onToggle={onToggle} disabled={isDisabled} />;
    })}
    </div>;
};